// React/Redux
import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";

// Custom Components
import MaterialModal from "../MaterialModal";
import FormControl from "@material-ui/core/FormControl";
import InputLabel from "@material-ui/core/InputLabel";
import Select from "@material-ui/core/Select";
import MenuItem from "@material-ui/core/MenuItem";
import RadioButton from "../../material-components/Misc/RadioButton";
import withModal from "../withModal";

// Actions
import { CloneFieldLabels } from "../../../actions/fieldLabelsActions";
import { getUserCompanies } from "../../../actions/adminCompaniesActions";
import { hideModal } from "../../../actions/actionCreators";
import { IAppState } from "../../../types/stores/index";

// helpers
import h from "../../../helpers";

type Props = { isOpen: boolean };

const CompanyCloneFieldLabelModal: React.FC<Props> = ({ isOpen }: Props) => {
    const dispatch = useDispatch();

    // State
    const [companyFrom, setCompanyFrom] = useState<number>(-1);
    const [companyTo, setCompanyTo] = useState<number>(-1);
    const [cloneOption, setCloneOption] = useState("clear");
    // Props
    const companies = useSelector((state: IAppState) => [...new Set(state.adminCompanies.userCompanies)]) || [];
    const userId = useSelector((state: IAppState) => state.session.userId || 0);

    useEffect(() => {
        if (isOpen && userId > 0) {
            dispatch(getUserCompanies());
        }
    }, [dispatch, userId, isOpen]);

    const hide = () => {
        resetValues();
        dispatch(hideModal("CLONE_COMPANY_LABELS"));
    };

    const handleChangeCompanyFrom = newValue => setCompanyFrom(newValue);

    const handleChangeCompanyTo = newValue => setCompanyTo(newValue);

    const getInValidMessage = () => {
        if (companyFrom == -1 || companyTo == -1) return "Company fields cannot be empty";

        if (companyFrom == companyTo)
            return "Company from and company to are the same. Please select unique companies.";

        return "";
    };

    const showMessage = message => {
        h.error(message);
    };

    const save = () => {
        const inValidMessage = getInValidMessage();
        if (inValidMessage) {
            showMessage(inValidMessage);
            return;
        }

        dispatch(CloneFieldLabels(companyFrom, companyTo, cloneOption));
        dispatch(hideModal("CLONE_COMPANY_LABELS"));
        resetValues();
    };

    const resetValues = () => {
        setCompanyFrom(-1);
        setCompanyTo(-1);
        setCloneOption("clear");
    };

    return (
        <MaterialModal
            id="variable-admin-modal"
            isOpen={isOpen}
            onClose={() => hide()}
            title="Clone Field Labels and Visibility"
            onPrimaryButtonRequest={() => save()}
            showPrimaryButton
            primaryButtonText="Clone"
            cancelButtonText="Cancel"
        >
            <FormControl style={{ minWidth: "15vw", paddingRight: "10px", paddingBottom: "30px" }}>
                <InputLabel>From Company</InputLabel>
                <Select
                    style={{ fontSize: "14px" }}
                    value={companyFrom == -1 ? "" : companyFrom}
                    onChange={e => handleChangeCompanyFrom(e.target.value)}
                    displayEmpty
                >
                    {companies
                        .filter(x => x.CompanyID != companyTo)
                        .map(x => (
                            <MenuItem key={`${"company_from_"}${x.CompanyID}`} value={x.CompanyID}>
                                {x.CompanyName}
                            </MenuItem>
                        ))}
                </Select>
            </FormControl>
            <FormControl style={{ minWidth: "15vw", paddingRight: "10px", paddingBottom: "30px" }}>
                <InputLabel>To Company</InputLabel>
                <Select
                    style={{ fontSize: "14px" }}
                    value={companyTo == -1 ? "" : companyTo}
                    onChange={e => handleChangeCompanyTo(e.target.value)}
                    displayEmpty
                >
                    {companies
                        .filter(x => x.CompanyID != companyFrom)
                        .map(x => (
                            <MenuItem key={`${"company_to_"}${x.CompanyID}`} value={x.CompanyID}>
                                {x.CompanyName}
                            </MenuItem>
                        ))}
                </Select>
            </FormControl>
            <RadioButton
                id="clone-option"
                name="cloneOption"
                options={[
                    { label: "Clear existing labels and clone", value: "clear" },
                    {
                        label: "Retain existing labels and clone only when missing field/visibility label",
                        value: "retain",
                    },
                ]}
                onChange={e => setCloneOption(e.target.value)}
                row={false}
                valChecked={cloneOption}
            />
        </MaterialModal>
    );
};

export default withModal(CompanyCloneFieldLabelModal, "CLONE_COMPANY_LABELS");
